import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../context/AuthContext";

function formatEventLabel(eventType) {
  switch (String(eventType || "").trim()) {
    case "LOGIN_SUCCESS":
      return "Login exitoso";
    case "LOGIN_FAILED":
      return "Login fallido";
    case "USER_CREATED":
      return "Usuario creado";
    case "USER_UPDATED":
      return "Usuario actualizado";
    case "USER_DEACTIVATED":
      return "Usuario desactivado";
    case "REPORT_GENERATED":
      return "Reporte generado";
    case "SENSOR_CLAIMED":
      return "Sensor vinculado";
    case "SENSOR_UNCLAIMED":
      return "Sensor desvinculado";
    case "AUDIT_TRAIL_EXPORTED":
      return "Audit trail exportado";
    default:
      return eventType || "Evento";
  }
}

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-EC", { timeZone: "America/Guayaquil" });
}

function formatSignatureAction(actionType) {
  switch (String(actionType || "").trim()) {
    case "USER_DEACTIVATION":
      return "Desactivación de usuario";
    case "USER_ROLE_CHANGE":
      return "Cambio de rol";
    case "USER_PERMISSION_CHANGE":
      return "Cambio de permisos";
    case "REPORT_APPROVAL":
      return "Aprobación de reporte";
    default:
      return actionType || "Acción";
  }
}

export default function Part11Page() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem("token");

  const canManageUsers = !!user?.permissions?.includes("manage_users");
  const canAccessPart11 = canManageUsers && !!user?.part11_enabled;
  const [loading, setLoading] = useState(true);
  const [pageMsg, setPageMsg] = useState("");
  const [summary, setSummary] = useState(null);
  const [signatureSummary, setSignatureSummary] = useState(null); 

  const authHeader = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const part11Enabled = !!summary?.team?.part11_enabled ?? !!user?.part11_enabled;
  const auditCount = summary?.totals?.audit_records ?? 0;

useEffect(() => {
  if (!canAccessPart11) return;

  const loadSummary = async () => {
    setLoading(true);
    setPageMsg("");

    try {
      const [part11Res, signaturesRes] = await Promise.all([
        fetch("/api/sensores/admin/part11-summary", {
          headers: authHeader,
        }),
        fetch("/api/sensores/admin/electronic-signatures-summary", {
          headers: authHeader,
        }),
      ]);

      const part11Data = await part11Res.json().catch(() => ({}));
      const signaturesData = await signaturesRes.json().catch(() => ({}));

      if (!part11Res.ok) {
        throw new Error(part11Data?.error || "No se pudo cargar el resumen Part 11");
      }

      if (!signaturesRes.ok) {
        throw new Error(signaturesData?.error || "No se pudo cargar el resumen de firmas electrónicas");
      }

      setSummary(part11Data);
      setSignatureSummary(signaturesData);
    } catch (err) {
      setPageMsg(err.message || "Error cargando el resumen Part 11");
    } finally {
      setLoading(false);
    }
  };

  loadSummary();
}, [authHeader, canAccessPart11]);




  const lastEvent = summary?.last_event;
  const lastExport = summary?.last_export;
  const lastCritical = summary?.last_critical_event;
  const signatureTotals = signatureSummary?.totals || { total: 0, pending: 0, signed: 0 };
  const lastSignatureCreated = signatureSummary?.last_created;
  const lastSignaturePending = signatureSummary?.last_pending;
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.mainTitle}>Cumplimiento CFR 21 Part 11</h1>
            <p style={styles.subtitle}>
              Equipo: <strong>{summary?.team?.name || user?.team || "Sin equipo"}</strong>
            </p>
          </div>

          <div style={styles.headerActions}>
            <button
              style={styles.secondaryBtn}
              onClick={() => navigate("/admin/audit-trail")}
            >
              Ver Audit Trail
            </button>

            <button style={styles.secondaryBtn} onClick={() => navigate(-1)}>
              Volver
            </button>
          </div>
        </div>

        {pageMsg ? <div style={styles.messageError}>{pageMsg}</div> : null}

        {loading ? (
          <div style={styles.card}>
            <p style={styles.text}>Cargando resumen de cumplimiento...</p>
          </div>
        ) : (
          <div style={styles.grid}>
            <div style={styles.card}>
              <div style={styles.sectionTitle}>Estado del módulo</div>

              <div style={styles.kpiGrid}>
                <div style={styles.kpiCard}>
                  <div style={styles.kpiLabel}>Modo Part 11</div>
                  <div
                    style={{
                      ...styles.kpiValue,
                      color: part11Enabled ? "#047857" : "#b91c1c",
                    }}
                  >
                    {part11Enabled ? "Activo" : "Inactivo"}
                  </div>
                </div>

                <div style={styles.kpiCard}>
                  <div style={styles.kpiLabel}>Audit Trail</div>
                  <div style={{ ...styles.kpiValue, color: "#047857" }}>
                    Activo
                  </div>
                </div>

                <div style={styles.kpiCard}>
                  <div style={styles.kpiLabel}>Exportación de evidencia</div>
                  <div style={{ ...styles.kpiValue, color: "#047857" }}>
                    Activa
                  </div>
                </div>

                <div style={styles.kpiCard}>
                  <div style={styles.kpiLabel}>Firma electrónica</div>
                  <div style={{ ...styles.kpiValue, color: "#b45309" }}>
                    Pendiente
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.sectionTitle}>Firma electrónica</div>

              <div style={styles.warningBox}>
                Este módulo está preparado para incorporar firma electrónica en
                acciones críticas. Por ahora, la trazabilidad se mantiene mediante
                usuario autenticado, motivo del cambio y audit trail.
              </div>

              <div style={styles.statusList}>
                <div style={styles.statusItem}>
                  <strong>Estado actual:</strong> En configuración
                </div>
                <div style={styles.statusItem}>
                  <strong>Integración:</strong> Pendiente de habilitación
                </div>
                <div style={styles.statusItem}>
                  <strong>Próximo objetivo:</strong> Confirmación reforzada para acciones críticas
                </div>
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.sectionTitle}>Acciones críticas previstas</div>

              <div style={styles.actionList}>
                <div style={styles.actionItem}>Cambios de rol y permisos</div>
                <div style={styles.actionItem}>Desactivación de usuarios</div>
                <div style={styles.actionItem}>Aprobación de reportes</div>
                <div style={styles.actionItem}>Otras acciones que el equipo defina como críticas</div>
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.sectionTitle}>Estado actual de controles</div>

              <div style={styles.controlList}>
                <div style={styles.controlItem}>
                  <span style={styles.controlOk}>●</span> Audit trail habilitado
                </div>
                <div style={styles.controlItem}>
                  <span style={styles.controlOk}>●</span> Exportación de audit trail habilitada
                </div>
                <div style={styles.controlItem}>
                  <span style={styles.controlOk}>●</span> Motivo obligatorio para cambios de usuarios
                </div>
                <div style={styles.controlItem}>
                  <span style={styles.controlPending}>●</span> Firma electrónica pendiente
                </div>
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.sectionTitle}>Resumen real del sistema</div>
              
  <div style={styles.card}>
  <div style={styles.sectionTitle}>Resumen de firma electrónica</div>

  <div style={styles.realDataGrid}>
    <div style={styles.realDataCard}>
      <div style={styles.realDataLabel}>Solicitudes totales</div>
      <div style={styles.realDataValue}>{signatureTotals.total || 0}</div>
    </div>

    <div style={styles.realDataCard}>
      <div style={styles.realDataLabel}>Pendientes</div>
      <div style={styles.realDataValue}>{signatureTotals.pending || 0}</div>
    </div>

    <div style={styles.realDataCard}>
      <div style={styles.realDataLabel}>Firmadas</div>
      <div style={styles.realDataValue}>{signatureTotals.signed || 0}</div>
    </div>

    <div style={styles.realDataCard}>
      <div style={styles.realDataLabel}>Última solicitud creada</div>
      <div style={styles.realDataText}>
        {lastSignatureCreated
          ? `${formatSignatureAction(lastSignatureCreated.action_type)} · ${lastSignatureCreated.executed_by_username || "—"}`
          : "Sin solicitudes"}
      </div>
      <div style={styles.realDataSub}>{fmtDate(lastSignatureCreated?.created_at)}</div>
    </div>

    <div style={styles.realDataCard}>
      <div style={styles.realDataLabel}>Última pendiente</div>
      <div style={styles.realDataText}>
        {lastSignaturePending
          ? `${formatSignatureAction(lastSignaturePending.action_type)} · ${lastSignaturePending.executed_by_username || "—"}`
          : "No hay pendientes"}
      </div>
      <div style={styles.realDataSub}>{fmtDate(lastSignaturePending?.created_at)}</div>
    </div>
  </div>
                 </div>


                <div style={styles.realDataGrid}>
                <div style={styles.realDataCard}>
                  <div style={styles.realDataLabel}>Total de registros auditados</div>
                  <div style={styles.realDataValue}>{auditCount}</div>
                </div>

                <div style={styles.realDataCard}>
                  <div style={styles.realDataLabel}>Último evento auditado</div>
                  <div style={styles.realDataText}>
                    {lastEvent
                      ? `${formatEventLabel(lastEvent.event_type)} · ${lastEvent.username || "—"}`
                      : "Sin registros"}
                  </div>
                  <div style={styles.realDataSub}>{fmtDate(lastEvent?.performed_at)}</div>
                </div>

                <div style={styles.realDataCard}>
                  <div style={styles.realDataLabel}>Última exportación de evidencia</div>
                  <div style={styles.realDataText}>
                    {lastExport
                      ? `${lastExport.username || "—"} exportó evidencia`
                      : "No hay exportaciones"}
                  </div>
                  <div style={styles.realDataSub}>{fmtDate(lastExport?.performed_at)}</div>
                </div>

                <div style={styles.realDataCard}>
                  <div style={styles.realDataLabel}>Último cambio crítico</div>
                  <div style={styles.realDataText}>
                    {lastCritical
                      ? `${formatEventLabel(lastCritical.event_type)} · ${lastCritical.username || "—"}`
                      : "No hay cambios críticos"}
                  </div>
                  <div style={styles.realDataSub}>{fmtDate(lastCritical?.performed_at)}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#e9f6fb",
    padding: "32px 16px",
  },
  container: {
    maxWidth: 1200,
    margin: "0 auto",
    display: "grid",
    gap: 18,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  headerActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  mainTitle: {
    margin: 0,
    color: "#0284c7",
    fontSize: "clamp(28px, 3vw, 40px)",
    fontWeight: 900,
  },
  subtitle: {
    margin: "6px 0 0 0",
    color: "#475569",
    fontSize: 16,
    fontWeight: 700,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 16,
  },
  card: {
    background: "#fff",
    border: "1px solid #dbeafe",
    borderRadius: 18,
    padding: 20,
    boxShadow: "0 8px 20px rgba(2,6,23,.06)",
    display: "grid",
    gap: 14,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 900,
    color: "#0f172a",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
  },
  kpiCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 16,
    display: "grid",
    gap: 6,
  },
  kpiLabel: {
    color: "#64748b",
    fontWeight: 800,
    fontSize: 13,
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 900,
  },
  warningBox: {
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    borderRadius: 14,
    padding: "14px 16px",
    fontWeight: 700,
    lineHeight: 1.5,
  },
  statusList: {
    display: "grid",
    gap: 10,
  },
  statusItem: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: "12px 14px",
    color: "#334155",
    fontWeight: 700,
  },
  actionList: {
    display: "grid",
    gap: 10,
  },
  actionItem: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: "12px 14px",
    fontWeight: 700,
    color: "#0f172a",
  },
  controlList: {
    display: "grid",
    gap: 10,
  },
  controlItem: {
    fontWeight: 800,
    color: "#334155",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  controlOk: {
    color: "#16a34a",
    fontSize: 16,
  },
  controlPending: {
    color: "#d97706",
    fontSize: 16,
  },
  realDataGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
  },
  realDataCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 16,
    display: "grid",
    gap: 6,
  },
  realDataLabel: {
    color: "#64748b",
    fontWeight: 800,
    fontSize: 13,
  },
  realDataValue: {
    fontSize: 28,
    fontWeight: 900,
    color: "#0f172a",
  },
  realDataText: {
    fontSize: 16,
    fontWeight: 800,
    color: "#0f172a",
    lineHeight: 1.4,
  },
  realDataSub: {
    color: "#64748b",
    fontWeight: 700,
    fontSize: 13,
  },
  primaryBtn: {
    border: "none",
    borderRadius: 12,
    padding: "11px 18px",
    background: "#2563eb",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  secondaryBtn: {
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    padding: "11px 16px",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
  },
  text: {
    margin: 0,
    color: "#334155",
    fontWeight: 700,
  },
  title: {
    margin: "0 0 10px 0",
    color: "#0f172a",
  },
  messageError: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
    borderRadius: 12,
    padding: "12px 14px",
    fontWeight: 800,
  },
};
